/**
 * Implement a class that represents a Bedrock client and provides a set of tools for converting messages to and from the LLM.
 * - sendThread(thread: Thread): Thread - send a message to the LLM
 */

import {
  type Message as BedrockMessage,
  BedrockRuntimeClient,
  type ContentBlock,
  ConverseCommand,
  type ConverseCommandInput,
  type ConverseResponse,
  type DocumentBlock,
  type ImageBlock,
  type SystemContentBlock,
  type ToolResultBlock,
  type ToolUseBlock,
  type VideoBlock,
  InvokeModelWithResponseStreamCommand,
} from "@aws-sdk/client-bedrock-runtime";
import { traceable } from "../tracing";
import { zodToJsonSchema } from "zod-to-json-schema";
import {
  type Attachment,
  Message,
  type MessageRole,
  type ToolCall,
} from "../message";
import type { Thread } from "../thread";
import { BaseModelDriver } from "./base";

const DEFAULT_MODEL = "anthropic.claude-3-haiku-20240307-v1:0";
const MAX_PROMPT_CHARACTERS = 320_000;
const CACHE_POINT_ENABLED = false;

/**
 * Available Bedrock model options
 */
export type BedrockModel = 
  // Anthropic Claude Models
  | "anthropic.claude-3-haiku-20240307-v1:0"
  | "anthropic.claude-3-sonnet-20240229-v1:0"
  | "anthropic.claude-3-opus-20240229-v1:0"
  | "anthropic.claude-instant-v1"
  | "anthropic.claude-v2"
  | "anthropic.claude-v2:1"
  
  // Amazon Titan Models
  | "amazon.titan-text-lite-v1"
  | "amazon.titan-text-express-v1"
  | "amazon.titan-text-premier-v1"
  | "amazon.titan-embed-text-v1"
  | "amazon.titan-embed-image-v1"
  | "amazon.titan-image-generator-v1"
  
  // AI21 Labs Models
  | "ai21.j2-mid-v1"
  | "ai21.j2-ultra-v1"
  | "ai21.jamba-instruct-v1:0"
  
  // Cohere Models
  | "cohere.command-text-v14"
  | "cohere.command-light-text-v14"
  | "cohere.embed-english-v3"
  | "cohere.embed-multilingual-v3"
  
  // Meta Models
  | "meta.llama2-13b-chat-v1"
  | "meta.llama2-70b-chat-v1"
  | "meta.llama3-8b-instruct-v1:0"
  | "meta.llama3-70b-instruct-v1:0"
  
  // Stability AI Models
  | "stability.stable-diffusion-xl-v1"
  | "stability.stable-diffusion-xl-v0"
  
  // Other models or custom model IDs
  | string;

export interface BedrockConfig {
  model?: BedrockModel;
  temperature?: number;
  maxTokens?: number;
  cache?: boolean;
  client?: BedrockRuntimeClient;
  trace?: boolean;
  traceMetadata?: Record<string, unknown>;
  reasoning_config?: Record<string, any>;
}

export class BedrockThreadDriver extends BaseModelDriver {
  private readonly decoder = new TextDecoder();
  private readonly client: BedrockRuntimeClient;
  private readonly cache: boolean;
  private readonly model: BedrockModel;
  private readonly temperature: number;
  private readonly maxTokens: number;
  private readonly reasoning_config?: Record<string, any>;

  constructor(config: BedrockConfig = {}) {
    super();
    if (
      !config.client &&
      (!process.env["AWS_ACCESS_KEY_ID"] ||
        !process.env["AWS_SECRET_ACCESS_KEY"] ||
        !process.env["AWS_REGION"])
    ) {
      throw new Error(
        "AWS_ACCESS_KEY_ID, AWS_SECRET_ACCESS_KEY and AWS_REGION environment variables must be set"
      );
    }

    let baseClient: BedrockRuntimeClient;

    if (config.client) {
      baseClient = config.client;
    } else {
      const accessKeyId = process.env["AWS_ACCESS_KEY_ID"];
      const secretAccessKey = process.env["AWS_SECRET_ACCESS_KEY"];
      const region = process.env["AWS_REGION"];

      if (!accessKeyId || !secretAccessKey || !region) {
        throw new Error("AWS credentials not properly configured");
      }

      baseClient = new BedrockRuntimeClient({
        region,
        credentials: {
          accessKeyId,
          secretAccessKey,
        },
      });
    }

    // Only wrap with tracing if trace is enabled (default to false)
    if (config.trace ?? false) {
      this.client = new Proxy(baseClient, {
        get: (target: BedrockRuntimeClient, prop: string | symbol, receiver) => {
          const originalValue = target[prop as keyof typeof target];

          if (prop === "send" && typeof originalValue === "function") {
            return traceable(originalValue.bind(target), {
              run_type: "llm",
              name: "bedrock",
              ...config.traceMetadata,
              aggregator: (args: any[]) => {
                const command = args[0] as ConverseCommand;
                const response = args[1] as ConverseResponse;
                return {
                  model: this.model,
                  input: {
                    messages: command.input.messages,
                    system: command.input.system,
                    inferenceConfig: command.input.inferenceConfig,
                  },
                  content: response.output?.message?.content,
                  metrics: response.metrics,
                  stopReason: response.stopReason,
                };
              },
            });
          }

          return Reflect.get(target, prop, receiver);
        },
      }) as BedrockRuntimeClient;
    } else {
      this.client = baseClient;
    }

    this.model = config.model || DEFAULT_MODEL;
    this.temperature = config.temperature || 0.7;
    this.maxTokens = config.maxTokens || 4096;
    this.cache = config.cache || false;
    this.reasoning_config = config.reasoning_config
  }

  /**
   * Convert attachments to Bedrock content blocks
   */
  private convertAttachmentsToContentBlocks(
    attachments: Attachment[]
  ): ContentBlock[] {
    return attachments.map((attachment): ContentBlock => {
      switch (attachment.type) {
        case "image":
          return {
            image: {
              format: attachment.format,
              source: attachment.source,
            } as ImageBlock,
          };
        case "document":
          return {
            document: {
              format: attachment.format,
              name: attachment.name,
              source: attachment.source,
            } as DocumentBlock,
          };
        case "video":
          return {
            video: {
              format: attachment.format,
              source: attachment.source,
            } as VideoBlock,
          };
      }
    });
  }

  /**
   * Convert thread messages to Bedrock format, separating system messages
   */
  private convertMessagesToBedrockFormat(messages: Message[]): {
    messages: BedrockMessage[];
    system: SystemContentBlock[];
  } {
    const systemMessages: SystemContentBlock[] = [];
    const conversationMessages: BedrockMessage[] = [];

    // Find the last non-system message and system message with cache true
    const lastCacheMessage = messages.find(
      (message) => message.cache && message.role !== "system"
    );
    const lastSystemCacheMessage = messages.find(
      (message) => message.cache && message.role === "system"
    );

    messages.forEach((message) => {
      if (message.role === "system") {
        systemMessages.push({
          text: message.content || "",
        });
        if (message === lastSystemCacheMessage && CACHE_POINT_ENABLED) {
          systemMessages.push({
            cachePoint: {
              type: "default",
            },
          } as any);
        }
        return;
      }

      const contentBlocks: ContentBlock[] = [];

      if (message.content && !message.toolResults?.length) {
        contentBlocks.push({ text: message.content });
      }

      // Add tool calls if present
      if (message.toolCalls?.length > 0) {
        contentBlocks.push(
          ...message.toolCalls.map(
            (call): ContentBlock => ({
              toolUse: {
                toolUseId: call.toolUseId,
                name: call.name,
                input: call.arguments,
              } as ToolUseBlock,
            })
          )
        );
      }

      // Add tool results if present
      if (message.toolResults?.length > 0) {
        contentBlocks.push(
          ...message.toolResults.map(
            (result): ContentBlock => ({
              toolResult: {
                toolUseId: result.toolUseId,
                content: [
                  {
                    text: JSON.stringify(result.result || result.error),
                  },
                ],
                status: result.error ? "error" : "success",
              } as ToolResultBlock,
            })
          )
        );

        if (message.content) {
          contentBlocks.push({ text: message.content });
        }
      }

      // Add attachment content blocks if present
      if (message.attachments.length > 0) {
        contentBlocks.push(
          ...this.convertAttachmentsToContentBlocks(message.attachments)
        );
      }

      // Add cache point if this is the last cache message
      if (message === lastCacheMessage && CACHE_POINT_ENABLED) {
        contentBlocks.push({
          cachePoint: {
            type: "default",
          },
        } as any);
      }

      conversationMessages.push({
        role: message.role === "tool" ? "user" : message.role,
        content: contentBlocks,
      });
    });

    return {
      messages: conversationMessages,
      system: systemMessages,
    };
  }

  /**
   * Parse tool calls from the LLM response content blocks
   */
  private parseToolCalls(content: ContentBlock[]): ToolCall[] {
    return content.reduce<ToolCall[]>((acc, block) => {
      if (
        "toolUse" in block &&
        block.toolUse &&
        typeof block.toolUse.name === "string" &&
        block.toolUse.input &&
        block.toolUse.toolUseId
      ) {
        try {
          acc.push({
            name: block.toolUse.name,
            toolUseId: block.toolUse.toolUseId,
            arguments: block.toolUse.input as Record<string, any>,
          });
        } catch (error) {
          console.error(
            `Failed to parse tool call input: ${block.toolUse.input}`
          );
        }
      }
      return acc;
    }, []);
  }

  /**
   * Send the thread state to the LLM and process the response
   */
  public async sendThread(thread: Thread): Promise<Thread> {
    const messages = thread.all();
    if (messages.length === 0) {
      throw new Error("Cannot send an empty thread");
    }

    const totalContent = messages.map((m) => m.content).join("\n");
    if (totalContent.length > MAX_PROMPT_CHARACTERS) {
      throw new Error("The thread content is too long");
    }

    try {
      const { messages: bedrockMessages, system } =
        this.convertMessagesToBedrockFormat(messages);
        
      const tools = thread.getTools();

      const toolConfig = tools.map((tool) => ({
        toolSpec: {
          name: tool.getName(),
          description: tool.getDescription(),
          inputSchema: {
            json: zodToJsonSchema(tool.getParameters()),
          },
        },
      })) as any[]; // Type assertion to resolve AWS SDK type mismatch

      const input: ConverseCommandInput = {
        modelId: this.model,
        messages: bedrockMessages,
        system,
        inferenceConfig: {
          maxTokens: this.maxTokens,
          temperature: this.temperature,
        },
        ...(this.reasoning_config ? {
          additionalModelRequestFields: {
            reasoning_config: this.reasoning_config,
          }
        } : {}),
        ...(tools.length > 0 ? {
          toolConfig: {
            tools: toolConfig,
            ...(thread.toolChoice === "any" ? { toolChoice: { any: {} } } : {}),
            ...(thread.toolChoice === "auto" ? { toolChoice: { auto: {} } } : {}),
          }
        } : {}),
      };

      const command = new ConverseCommand(input);
      const response = (await this.client.send(command)) as ConverseResponse;

      if (!response.output?.message?.content) {
        throw new Error("No response received from Bedrock");
      }

      const responseContent = response.output.message.content;
      const responseText = responseContent
        .filter((c) => "text" in c && c.text)
        .map((c) => c.text)
        .join("\n");
      const reasoning = responseContent
        .filter((c) => "reasoningContent" in c) 
        .map((c: any) => c?.reasoningContent?.reasoningText)

      const responseMessage = new Message({
        content: responseText,
        reasoning,
        role: "assistant" as MessageRole,
        toolCalls: this.parseToolCalls(responseContent),
        cache: this.cache,
      });

      thread.push(responseMessage);

      // Log usage metrics if available
      if (response.usage) {
        this.debug("Bedrock usage metrics", {
          inputTokens: response.usage.inputTokens,
          outputTokens: response.usage.outputTokens,
          totalTokens: response.usage.totalTokens,
        });
      }

      return thread;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      this.error(
        `Failed to send thread: ${errorMessage} (thread length: ${thread.all().length})`
      );
      throw error;
    }
  }

  /**
   * Stream the thread state to the LLM and process the response chunk by chunk
   */
  public async *streamThread(thread: Thread): AsyncGenerator<{ stream: AsyncGenerator<string, void>, message: Message }, Thread> {
    this.validateThread(thread);

    try {
      const { formattedMessages, systemMessage } = this.formatMessagesForStreaming(thread.all());
      const input = this.createStreamingInput(formattedMessages, systemMessage);

      this.debug("Streaming request:", JSON.parse(input.body));

      const command = new InvokeModelWithResponseStreamCommand(input);
      const response = await this.client.send(command);

      if (!response.body) {
        throw new Error("No response stream received from Bedrock");
      }

      const initialMessage = new Message({
        content: "",
        role: "assistant",
        toolCalls: [],
        cache: this.cache,
      });

      const stream = this.createStreamProcessor(response.body, initialMessage);
      yield { stream, message: initialMessage };

      thread.push(initialMessage);
      return thread;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      this.error(
        `Failed to stream thread: ${errorMessage} (thread length: ${thread.all().length})`
      );
      throw error;
    }
  }

  /**
   * Validate thread content length and emptiness
   */
  private validateThread(thread: Thread): void {
    const messages = thread.all();
    if (messages.length === 0) {
      throw new Error("Cannot send an empty thread");
    }

    const totalContent = messages.map((m) => m.content).join("\n");
    if (totalContent.length > MAX_PROMPT_CHARACTERS) {
      throw new Error("The thread content is too long");
    }
  }

  /**
   * Format messages for streaming request
   */
  private formatMessagesForStreaming(messages: Message[]): {
    formattedMessages: { role: string; content: { type: string; text: string }[] }[];
    systemMessage: string | undefined;
  } {
    const formattedMessages = messages
      .filter(msg => msg.role !== "system")
      .map(msg => ({
        role: msg.role === "tool" ? "user" : msg.role,
        content: msg.content ? [{ type: "text", text: msg.content }] : [],
      }));

    const systemMessage = messages.find(msg => msg.role === "system")?.content;

    return { formattedMessages, systemMessage };
  }

  /**
   * Create streaming request input
   */
  private createStreamingInput(formattedMessages: any[], systemMessage?: string) {
    return {
      modelId: this.model,
      contentType: "application/json",
      accept: "application/json",
      body: JSON.stringify({
        anthropic_version: "bedrock-2023-05-31",
        messages: formattedMessages,
        ...(systemMessage ? { system: systemMessage } : {}),
        max_tokens: this.maxTokens,
        temperature: this.temperature,
      }),
    };
  }

  /**
   * Process a single chunk from the Bedrock stream
   */
  private processStreamChunk(
    chunk: { chunk?: { bytes?: Uint8Array } },
    accumulatedText: string,
    currentMessage: Message
  ): { newText: string | null; updatedMessage: Message } {
    if (!chunk.chunk?.bytes) {
      return { newText: null, updatedMessage: currentMessage };
    }

    try {
      const decodedChunk = this.decoder.decode(chunk.chunk.bytes);
      const parsed = JSON.parse(decodedChunk);
      
      if (!parsed.delta?.text) {
        return { newText: null, updatedMessage: currentMessage };
      }

      const newText = parsed.delta.text;
      if (accumulatedText.endsWith(newText)) {
        return { newText: null, updatedMessage: currentMessage };
      }

      const updatedMessage = new Message({
        content: accumulatedText + newText,
        role: "assistant",
        toolCalls: [],
        cache: this.cache,
      });

      return { newText, updatedMessage };
    } catch (e) {
      return { newText: null, updatedMessage: currentMessage };
    }
  }

  /**
   * Create a stream processor for handling Bedrock response chunks
   */
  private async *createStreamProcessor(
    responseBody: AsyncIterable<{ chunk?: { bytes?: Uint8Array } }>,
    initialMessage: Message
  ): AsyncGenerator<string, void> {
    let accumulatedText = "";
    let currentMessage = initialMessage;

    for await (const chunk of responseBody) {
      const { newText, updatedMessage } = this.processStreamChunk(
        chunk,
        accumulatedText,
        currentMessage
      );

      if (newText) {
        accumulatedText += newText;
        currentMessage = updatedMessage;
        yield newText;
      }
    }
  }
}
